import { WORK_ITEM_KEY_RE } from './work-types';

export type AssignedQueueItem = {
  id: string;
  key: string;
  priority: string;
  due_date: string | null;
};

export type AssignedSummary = {
  urgent: number;
  overdue: number;
  dueThisWeek: number;
  noDueDate: number;
};

export const QUEUE_FOCUS = ['urgent', 'overdue', 'this_week', 'no_due'] as const;
export type QueueFocus = (typeof QUEUE_FOCUS)[number];

export function isQueueFocus(value: string): value is QueueFocus {
  return (QUEUE_FOCUS as readonly string[]).includes(value);
}

export function parseQueueFocus(raw: string | null): QueueFocus | null {
  if (raw == null || raw === '') return null;
  return isQueueFocus(raw) ? raw : null;
}

export function matchesQueueFocus(
  item: AssignedQueueItem,
  focus: QueueFocus | null,
  today: Date,
): boolean {
  if (focus == null) return true;
  if (focus === 'urgent') return item.priority === 'urgent';
  if (focus === 'overdue') return isOverdue(item.due_date, today);
  if (focus === 'this_week') return isDueThisWeek(item.due_date, today);
  return item.due_date == null || item.due_date === '';
}

const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export function parseDateOnly(value: string): Date | null {
  if (!DATE_ONLY.test(value)) return null;
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return null;
  }
  return date;
}

export function startOfDay(value: Date): Date {
  return new Date(value.getFullYear(), value.getMonth(), value.getDate());
}

function endOfLocalWeek(today: Date): Date {
  const start = startOfDay(today);
  const weekday = start.getDay();
  const addDays = weekday === 0 ? 0 : 7 - weekday;
  return new Date(start.getFullYear(), start.getMonth(), start.getDate() + addDays);
}

export function isOverdue(dueDate: string | null, today: Date): boolean {
  if (!dueDate) return false;
  const parsed = parseDateOnly(dueDate);
  if (!parsed) return false;
  return parsed.getTime() < startOfDay(today).getTime();
}

export function isDueThisWeek(dueDate: string | null, today: Date): boolean {
  if (!dueDate) return false;
  const parsed = parseDateOnly(dueDate);
  if (!parsed) return false;
  const start = startOfDay(today).getTime();
  const end = endOfLocalWeek(today).getTime();
  const time = parsed.getTime();
  return time >= start && time <= end;
}

/** Done (completed group) is off Assigned to me. Unknown group stays visible. */
export function isOpenAssignedStateGroup(group: string | undefined): boolean {
  return group !== 'completed';
}

export function isAssignedException(item: AssignedQueueItem, today: Date): boolean {
  return item.priority === 'urgent' || isOverdue(item.due_date, today);
}

function exceptionRank(item: AssignedQueueItem, today: Date): number {
  if (isOverdue(item.due_date, today)) return 0;
  if (item.priority === 'urgent') return 1;
  if (isDueThisWeek(item.due_date, today)) return 2;
  if (item.priority === 'high') return 3;
  return 4;
}

export function summarizeAssigned(items: AssignedQueueItem[], today: Date): AssignedSummary {
  return items.reduce<AssignedSummary>(
    (summary, item) => {
      if (item.priority === 'urgent') summary.urgent += 1;
      if (isOverdue(item.due_date, today)) summary.overdue += 1;
      if (isDueThisWeek(item.due_date, today)) summary.dueThisWeek += 1;
      if (item.due_date == null || item.due_date === '') summary.noDueDate += 1;
      return summary;
    },
    { urgent: 0, overdue: 0, dueThisWeek: 0, noDueDate: 0 },
  );
}

const PRIORITY_RANK: Record<string, number> = {
  urgent: 0,
  high: 1,
  medium: 2,
  low: 3,
  none: 4,
};

function dueTime(dueDate: string | null): number | null {
  if (!dueDate) return null;
  const parsed = parseDateOnly(dueDate);
  return parsed ? parsed.getTime() : null;
}

/** Sort assigned/queue rows. Unknown `orderBy` values keep the focus ranking. */
export function sortQueueItems<T extends AssignedQueueItem & { title: string; created_at?: string }>(
  items: T[],
  orderBy: string | null,
  today: Date,
): T[] {
  if (orderBy === 'due_date' || orderBy === '-due_date') {
    const newestFirst = orderBy === '-due_date';
    return [...items].sort((left, right) => {
      const leftDue = dueTime(left.due_date);
      const rightDue = dueTime(right.due_date);
      if (leftDue == null && rightDue == null) return 0;
      if (leftDue == null) return 1;
      if (rightDue == null) return -1;
      return newestFirst ? rightDue - leftDue : leftDue - rightDue;
    });
  }
  if (orderBy === 'priority') {
    return [...items].sort((left, right) => {
      const leftRank = PRIORITY_RANK[left.priority] ?? 5;
      const rightRank = PRIORITY_RANK[right.priority] ?? 5;
      if (leftRank !== rightRank) return leftRank - rightRank;
      return left.title.localeCompare(right.title);
    });
  }
  if (orderBy === '-created_at') {
    return [...items].sort((left, right) => {
      const leftAt = left.created_at ?? '';
      const rightAt = right.created_at ?? '';
      return rightAt.localeCompare(leftAt);
    });
  }
  return sortAssignedItems(items, today);
}

export function sortAssignedItems<T extends AssignedQueueItem>(items: T[], today: Date): T[] {
  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const rank = exceptionRank(a.item, today) - exceptionRank(b.item, today);
      return rank !== 0 ? rank : a.index - b.index;
    })
    .map((entry) => entry.item);
}

export function identifierFromKey(key: string): string | null {
  if (!WORK_ITEM_KEY_RE.test(key)) return null;
  const dash = key.lastIndexOf('-');
  if (dash <= 0) return null;
  return key.slice(0, dash).toUpperCase();
}

/** Letter shown on a project tile, falling back to the identifier if the name is blank. */
export function projectInitial(name: string, identifier: string): string {
  return (name.trim() || identifier).charAt(0).toUpperCase();
}
